import React from 'react';
import PropTypes from 'prop-types';
import { CircularProgressbar } from 'react-circular-progressbar';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import SVG from 'react-inlinesvg';

import PrizeComponent from '../../PrizeComponent/PrizeComponent.jsx';
import Close from '../../../../javascript/assets/Close.svg';
import Maximize from '../../../assets/Maximize.svg';
import { Up } from '../../../assets';
import SmallGift from '../../../assets/SmallGift.svg';
import Text from '../../Text';
import useRaffleProgress from '../hooks/useRaffleProgress';
import Modal from '../../../shared/Modal/Modal.jsx';

const RaffleProgressBarComponent = ({ progressBarData, percentage }) => {
  const { state, timeLeft } = useRaffleProgress({
    percentage,
    remainingInfo: progressBarData.raffle.remainingInfo,
  });

  const openPrizesModal = () => {
    Modal({
      component: (
        <>
          <CloseButton onClick={() => Swal.close()}>
            <img src={Close} alt="close" />
          </CloseButton>
          <PrizeComponent
            raffle={progressBarData.raffle}
            ticketsCount={progressBarData.raffleTickets}
          />
        </>
      ),
      padding: '0px',
      width: '360px',
    });
  };

  return (
    <RaffleSection className="round_bar">
      <RaffleLabel variant="subtitle2">Entries</RaffleLabel>
      <ProgressRadial className="progress-radial">
        <CircularProgressbar value={state} />
        <EntryValue variant="h6" id="entries">
          {progressBarData.raffleTickets || '0'}
        </EntryValue>
        <RaffleLabelIcon src={SmallGift} alt="gift_image" />
      </ProgressRadial>

      <RaffleData as="div" bg="white" variant="subtitle2" id="raffle_data">
        {timeLeft}
      </RaffleData>
      <Expand onClick={openPrizesModal} id="raffle_data_expand">
        <ExpandImg src={Maximize} />
        <MobileExpandIcon>
          <SVG src={Up} />
        </MobileExpandIcon>
      </Expand>
    </RaffleSection>
  );
};

RaffleProgressBarComponent.propTypes = {
  progressBarData: PropTypes.shape({
    raffle: PropTypes.object,
    raffleTickets: PropTypes.number,
  }),
  percentage: PropTypes.number,
};

export default RaffleProgressBarComponent;

const Expand = styled.div`
  cursor: pointer;
  min-height: 60px;
  min-width: 74px;
  position: absolute;
  background: transparent;
  display: flex;
  justify-content: center;
  right: 0;

  ${themeGet('mediaQueries.forMediumUp')} {
    position: static;
    width: 50px;
    border-radius: 0px 20px 20px 0px;
    background: ${themeGet('colors.white')};
    min-width: auto;
    min-height: auto;
    height: 34px;
  }
`;

const ExpandImg = styled(SVG)`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  display: none;

  &:hover {
    path {
      stroke: ${themeGet('colors.primary.80')};
    }
  }
  &:active {
    path {
      stroke: ${themeGet('colors.primary.120')};
    }
  }

  ${themeGet('mediaQueries.forMediumUp')} {
    display: block;
  }
`;

const MobileExpandIcon = styled.div`
  height: 9px;
  width: 23px;
  border-radius: 2px;
  background: ${themeGet('colors.black.10')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${themeGet('mediaQueries.forMediumUp')} {
    display: none;
  }
`;

const ProgressRadial = styled.div`
  margin: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: white;
  box-shadow: none;
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;

  ${themeGet('mediaQueries.forMediumUp')} {
    width: 50px;
    height: 50px;
  }
`;

const RaffleSection = styled.div`
  display: flex;
  align-items: center;
  padding: 0 9px;
  border-radius: 4px;
  min-width: 74px;
  width: 74px;
  height: 100%;
  border-left: 1px solid ${themeGet('colors.black.10')};
  position: relative;

  ${themeGet('mediaQueries.forMediumUp')} {
    padding: 0;
    width: 260px;
    border: none;
  }
`;

const RaffleLabel = styled(Text)`
  padding: 0;
  display: none;
  justify-content: center;
  align-items: center;
  border-radius: 20px 0px 0px 20px;
  margin-right: -10px;
  height: 34px;
  background: ${themeGet('colors.white')};
  color: ${themeGet('colors.black.100')};

  ${themeGet('mediaQueries.forMediumUp')} {
    display: flex;
    width: 90px;
    padding: 0 24px 0 16px;
  }
`;

const RaffleData = styled(Text)`
  display: none;

  ${themeGet('mediaQueries.forMediumUp')} {
    white-space: nowrap;
    display: flex;
    align-items: center;
    width: 90px;
    height: 34px;
    padding: 0 8px 0 20px;
    text-transform: capitalize;
    margin: 0 0 0 -10px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: -25px;
  right: 0;

  ${themeGet('mediaQueries.forMediumUp')} {
    right: -25px;
  }
`;

const EntryValue = styled(Text)`
  color: ${themeGet('colors.primary.100')};
  position: absolute;
  right: -20px;

  ${themeGet('mediaQueries.forMediumUp')} {
    font-weight: 600;
    position: static;
    right: 0;
  }
`;

const RaffleLabelIcon = styled.img`
  display: block;
  width: 16px;
  height: 16px;

  ${themeGet('mediaQueries.forMediumUp')} {
    display: none;
  }
`;
